import { ChildProps } from '@apollo/client/react/hoc';
import { gql, useApolloClient, ApolloClient } from '@apollo/client';
import React from 'react';
import { CoreMarketplaceCspRow } from '@reverbdotcom/commons/src/gql/graphql';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import { conditionFilter } from './condition_filter';
import { Location } from 'history';

import CSPRow, { CSPCardFragment } from '@reverbdotcom/commons/src/components/csp_row';
import { MarketplacePathContext } from '../../shared/url_helpers';
import { IUserContext, withUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { AUTODIRECTS_GROUP, cacheAutodirect, contexts, isCached } from '../../marketplace/marketplace_autodirects';
import { mapUrlParamsForCSP } from '../../marketplace/map_params';
import { showLocalizedListingContent } from '@reverbdotcom/commons/src/localize_listing_rows_utils';

interface ExternalProps {
  title: string;
  location: Partial<Location>;
  autodirect?: Partial<Location>;
  routeParams: MarketplacePathContext;
  trackingQuery?: string;
}

type CombinedProps = IUserContext & ExternalProps;
type IProps = ChildProps<CombinedProps, CoreMarketplaceCspRow.Query>;

export function queryVariablesWithAutodirectContext(
  props: IProps,
) {
  const { routeParams, location, location: { query }, user } = props;

  return {
    ...mapUrlParamsForCSP(routeParams, query, user),
    autodirects: AUTODIRECTS_GROUP,
    contexts: contexts(location),
  };
}

function applyAutodirect(client: ApolloClient<any>, props: IProps) {
  const { autodirect, data } = props;
  const args = { props, client, autodirect, query: marketplaceCspRowQuery, mapArgs: queryVariablesWithAutodirectContext };

  if (!data || data.loading || !autodirect || isCached(args)) { return; }

  cacheAutodirect({ ...args, data });
}

const COMPONENT_NAME = 'MarketplaceCSPRow';

export function MarketplaceCSPRow(props: IProps) {
  const apolloClient = useApolloClient();
  const loading = props.data?.loading;
  const csps = props.data?.cspSearch?.csps || [];
  const eligible = !loading && !!csps.length;
  const mParticleViewData = {
    componentName: COMPONENT_NAME,
    cspCount: csps.length,
    eventName: MParticleEventName.ComponentView,
    query: props.trackingQuery,
  };

  useViewTracking(mParticleViewData, eligible);
  applyAutodirect(apolloClient, props);

  return (
    <CSPRow
      title={props.title}
      csps={csps}
      loading={loading}
      conditionFilter={conditionFilter(props.location.query.condition)}
      trackingComponentName={COMPONENT_NAME}
      shouldTrack
      trackingQuery={props.trackingQuery}
      hidePrices={showLocalizedListingContent(props.user)}
      expectedCount={6}
    />
  );
}

export const marketplaceCspRowQuery = gql`
  query Core_Marketplace_CspRow(
    $autodirects: reverb_search_Autodirects
    $brands: [String],
    $categories: [String],
    $condition: String,
    $contexts: [reverb_search_Context],
    $countryOfOrigin: [String],
    $curatedSetSlugs: [String],
    $currency: String
    $decades: [String],
    $fullTextQuery: String,
    $listingsThatShipTo: String
    $priceMin: String,
    $priceMax: String,
    $sort: reverb_search_CSPSearchRequest_Sort,
    $traitValues: [String],
    $yearMax: Int,
    $yearMin: Int,
  ) {
    cspSearch(input: {
      autodirects: $autodirects,
      brandSlugs: $brands,
      categorySlugs: $categories,
      condition: $condition,
      contexts: $contexts,
      countryOfOrigin: $countryOfOrigin,
      curatedSetSlugs: $curatedSetSlugs,
      currency: $currency,
      decades: $decades,
      fullTextQuery: $fullTextQuery,
      fullTextQueryOperand: AND,
      priceMin: $priceMin,
      priceMax: $priceMax,
      sort: $sort,
      listingsThatShipTo: $listingsThatShipTo,
      traitValues: $traitValues,
      withInventory: true,
      yearMax: $yearMax,
      yearMin: $yearMin,
    }) {
      csps {
        _id
        ... CSPCard
        }
    }
  }
  ${CSPCardFragment}
`;

const connect = withGraphql<
CombinedProps,
CoreMarketplaceCspRow.Query,
CoreMarketplaceCspRow.Variables
>(
  marketplaceCspRowQuery,
  {
    skip: (props) => {
      const { shop, curated_set_slug, landing_page_slug } = props.routeParams;

      if (shop || curated_set_slug || landing_page_slug) {
        return true;
      }

      return false;
    },
    options: (props) => {
      return {
        ssr: true,
        variables: {
          ...queryVariablesWithAutodirectContext(props),
        },
      };
    },
  },
);

export default withUserContext(connect(MarketplaceCSPRow));
